import * as React from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  View,
  ImageBackground,
} from "react-native";
import { useSafeArea } from "react-native-safe-area-context";
import { ScreenProps } from "./screen.props";
import { isNonScrolling, offsets, presets } from "./screen.presets";
import { useTheme } from "react-native-paper";
import { ThemeType } from "../../theme/theme";
import { useRef } from "react";

const isIos = Platform.OS === "ios";

const renderBackgroundImage = (props: ScreenProps) => {
  if (props.backgroundImageSrc) {
    return <ImageBackground source={props.backgroundImageSrc} style={props.backgroundImageStyle} />;
  }
};

function ScreenWithoutScrolling(props: ScreenProps) {
  const theme = useTheme() as ThemeType;
  const insets = useSafeArea();
  const preset = presets.fixed;
  const style = props.style || {};
  const dontUseAutoPadding = props.dontUseAutoPadding;
  const backgroundStyle = props.backgroundColor ? { backgroundColor: props.backgroundColor } : {};
  const insetStyle = {
    paddingTop: props.unsafe ? 0 : insets.top,
    paddingRight: dontUseAutoPadding ? 0 : theme.spacing.medium,
    paddingLeft: dontUseAutoPadding ? 0 : theme.spacing.medium,
  };
  const banner = props.renderBanner;
  return (
    <KeyboardAvoidingView
      style={[preset.outer, backgroundStyle]}
      behavior={isIos ? "padding" : null}
      keyboardVerticalOffset={offsets[props.keyboardOffset || "none"]}>
      <StatusBar barStyle={props.statusBar || "light-content"} />
      {banner ? banner : null}
      {renderBackgroundImage(props)}
      <View style={[preset.inner, style, insetStyle]}>{props.children}</View>
    </KeyboardAvoidingView>
  );
}

function ScreenWithScrolling(props: ScreenProps) {
  const theme = useTheme() as ThemeType;
  const preset = presets.scroll;
  const style = props.style || {};
  const dontUseAutoPadding = props.dontUseAutoPadding;
  const backgroundStyle = props.backgroundColor ? { backgroundColor: props.backgroundColor } : {};
  const insetStyle = {
    paddingRight: dontUseAutoPadding ? 0 : theme.spacing["medium"],
    paddingLeft: dontUseAutoPadding ? 0 : theme.spacing["medium"],
  };
  const banner = props.renderBanner;
  const scrollViewRef = useRef<ScrollView>();
  if (props.scrollToTop && scrollViewRef.current) {
    scrollViewRef.current.scrollTo(0);
  }

  return (
    <KeyboardAvoidingView
      style={[preset.outer, backgroundStyle]}
      behavior={isIos ? "padding" : "height"}
      keyboardVerticalOffset={offsets[props.keyboardOffset || "none"]}>
      <StatusBar barStyle={props.statusBar || "light-content"} />
      {banner ? banner : null}
      {renderBackgroundImage(props)}
      <View style={[preset.outer, backgroundStyle, insetStyle]}>
        <ScrollView
          ref={scrollViewRef}
          keyboardShouldPersistTaps={props.keyboardShouldPersistTaps || "always"}
          style={[preset.outer, backgroundStyle]}
          contentContainerStyle={[preset.inner, style]}>
          {props.children}
        </ScrollView>
      </View>
    </KeyboardAvoidingView>
  );
}

/**
 * The starting component on every screen in the app.
 *
 * @param props The screen props
 */
export function Screen(props: ScreenProps) {
  if (isNonScrolling(props.preset)) {
    return <ScreenWithoutScrolling {...props} />;
  } else {
    return <ScreenWithScrolling {...props} />;
  }
}
